"use client";

import React, { useState, useEffect } from "react";
import { Eye, EyeOff, History, Key } from "lucide-react";
import { toast } from "sonner";
import crypto from "crypto";
import { Button } from "../../components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../../components/ui/card";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Skeleton } from "../../components/ui/skeleton";
import { useSidebar } from "../../components/ui/sidebar";
import { post } from "../../lib/api";
import PageHeader from "../../components/Page/page_header";
import "../../App.css";
import "./settings.css";
import { useTranslation } from "react-i18next";
import { Sparkles } from "lucide-react";
import { Avatar, AvatarFallback, AvatarImage } from "../../components/ui/avatar";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";


export default function Settings({ defaultValues }) {
  const [isLoading, setIsLoading] = useState(true);
  const [recentActivity, setRecentActivity] = useState([]);
  const { user } = useSidebar();
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Hash a password using sha256
  const hashPassword = (passwordToHash) => {
    const hash = crypto.createHash("sha256");
    hash.update(passwordToHash);
    return hash.digest("hex");
  };

  useEffect(() => {
    const fetchRecentActivity = async () => {
      try {
        setIsLoading(true);
        const response = await fetch(`${process.env.REACT_APP_CMC_API}/statistics?method=getSessions&user_id=${user[0]}`);
        const data = await response.json();
        setRecentActivity(data);
      } catch (error) {
        console.error("Failed to fetch recent activity:", error);
        toast.error("Failed to load recent activity");
      } finally {
        setIsLoading(false);
      }
    };

    if (user && user[0]) {
      fetchRecentActivity();
    }
  }, [user]);

  // Change the current user's password
  const handleChangePassword = async () => {
    if (password.length < 1) {
      toast.error("Password cannot be empty");
      return;
    }

    if (!user) {
      toast.error("User information not available");
      return;
    }

    setIsSubmitting(true);
    try {
      const hashedPassword = hashPassword(password);
      const response = await post({ method: "changePassword", user, password: hashedPassword });

      if (response.status === "error") {
        throw new Error(response.message);
      }

      toast.success(`Password for user ${user[1]} changed successfully`);
      setPassword("");
    } catch (error) {
      toast.error("Failed to change password");
      console.error(error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const { t } = useTranslation();
  // Get the current brand value from the app context
  const { brand, setMode } = useSidebar();
  // State to manage whether the Change Profile Picture modal is open or closed
  // const [isChangeProfileOpen, setChangeProfileOpen] = useState(false);
  // Function to fetch the brand logo based on the brand name
  const fetchBrand = (brandName) => {
    return `${process.env.PUBLIC_URL}/Logos/${brandName}.png`;
  };

  useEffect(() => {
    const mediaQuery = window.matchMedia("(prefers-color-scheme: dark)");
    const handler = (event) => {
      const newColorScheme = event.matches ? "dark" : "light";
      setMode(newColorScheme);
    };
    mediaQuery.addEventListener("change", handler);
    return () => {
      mediaQuery.removeEventListener("change", handler);
    };
  }, []);



  return (
    <div className="container mx-auto py-8 px-4 !w-screen mt-12 space-y-8">
      <PageHeader title="Settings" />


      <div className="w-120 max-w-2xl space-y-8 p-6 bg-white/50 dark:bg-[var(--header-color-dark)] rounded-xl border border-zinc-200/80 dark:border-zinc-800/80 shadow-xs ">
          <div className="flex items-center justify-center gap-6">
            <Avatar className="h-24 w-24 rounded-full border-2 border-zinc-200/80 dark:border-zinc-800/80 shadow-xs">
              <AvatarImage src={fetchBrand(brand)} className="rounded-full object-cover " />
              <AvatarFallback className="bg-[#b19cd9] dark:bg-zinc-900 text-white font-medium text-lg text-center">{brand}</AvatarFallback>
            </Avatar>
            <Button
                variant="outline"
                className="h-24 w-24 rounded-full border-2 border-dashed border-zinc-200/80 dark:border-zinc-800/80
                             hover:border-zinc-300 dark:hover:border-zinc-700 hover:bg-zinc-50 dark:hover:bg-zinc-900/50
                             transition-colors shadow-sm"
            >
              <Sparkles className="h-6 w-6 text-zinc-600 dark:text-zinc-400" />

            </Button>
          </div>

          <div className="grid gap-6">
            <div className="grid gap-2">
              <Label htmlFor="name" className="text-zinc-700 dark:text-zinc-300">
                Display Name
              </Label>
              <Input
                  id="name"
                  // placeholder="Your brand name"
                  defaultValue={brand}
                  autoComplete="off"
                  className="bg-white dark:bg-zinc-900/50 border-zinc-200/80 dark:border-zinc-800/80
                                 focus:border-zinc-300 dark:focus:border-zinc-700
                                 focus:ring-1 focus:ring-zinc-200 dark:focus:ring-zinc-800
                                 placeholder:text-zinc-400 dark:placeholder:text-zinc-600 text-black"
              />
            </div>

            <div className="grid gap-2">
              <Label htmlFor="theme" className="text-zinc-700 dark:text-zinc-300">
                Theme
              </Label>
              <p className="text-sm text-zinc-500 dark:text-zinc-400">Choose your preferred color scheme</p>
              <Select defaultValue="system">
                <SelectTrigger className="bg-white dark:bg-zinc-900/50 border-zinc-200/80 dark:border-zinc-800/80 text-black">
                  <SelectValue placeholder="Select theme" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem onClick={ () => setMode("system")} value="system">System</SelectItem>
                  <SelectItem onClick={ () => setMode("light")} value="light">Light</SelectItem>
                  <SelectItem onClick={ () => setMode("dark")} value="dark">Dark</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/*<div className="grid gap-2">*/}
            {/*  <Label htmlFor="language" className="text-zinc-700 dark:text-zinc-300">*/}
            {/*    Language*/}
            {/*  </Label>*/}
            {/*  <p className="text-sm text-zinc-500 dark:text-zinc-400">Choose your preferred language</p>*/}
            {/*  <Select defaultValue="english">*/}
            {/*    <SelectTrigger className="bg-white dark:bg-zinc-900/50 border-zinc-200/80 dark:border-zinc-800/80 text-black">*/}
            {/*      <SelectValue placeholder="Select language" />*/}
            {/*    </SelectTrigger>*/}
            {/*    <SelectContent>*/}
            {/*      <SelectItem value="english">English</SelectItem>*/}
            {/*      <SelectItem value="german">German</SelectItem>*/}
            {/*      <SelectItem value="dutch">Dutch</SelectItem>*/}
            {/*    </SelectContent>*/}
            {/*  </Select>*/}
            {/*</div>*/}

          </div>
          <div className="flex justify-end gap-4 ">
            <Button  className="bg-purple-700 dark:bg-purple-700 hover:bg-purple-900 dark:hover:bg-purple-900 text-white">
              Cancel
            </Button>
            <Button
                className="bg-purple-700 dark:bg-purple-700 hover:bg-purple-900 dark:hover:bg-purple-900 text-white">Save Changes</Button>
          </div>
        </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">

        {/* Password Management Card */}
        <Card className={"dark:bg-[var(--header-color-dark)]"}>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Key className="h-5 w-5" />
              Password Management
            </CardTitle>
            <CardDescription>Update your account password</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="new-password">New Password</Label>
                <div className="relative">
                  <Input id="new-password" type={showPassword ? "text" : "password"} value={password} onChange={(e) => setPassword(e.target.value)} className="pr-10" />
                  <Button type="button" variant="ghost" size="icon" className="absolute right-0 top-0 h-full px-3" onClick={() => setShowPassword(!showPassword)}>
                    {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    <span className="sr-only">{showPassword ? "Hide password" : "Show password"}</span>
                  </Button>
                </div>
              </div>
              <Button onClick={handleChangePassword} disabled={isSubmitting || !password} className="w-full sm:w-auto bg-purple-700 hover:bg-purple-800 text-white">
                {isSubmitting ? "Updating..." : "Update Password"}
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Recent Activity Card */}
        <Card className={"dark:bg-[var(--header-color-dark)]"}>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <History className="h-5 w-5" />
              Recent Activity
            </CardTitle>
            <CardDescription>View your login history from the past 7 days</CardDescription>
          </CardHeader>
          <CardContent>
            {isLoading ? (
              <div className="space-y-2">
                <Skeleton className="h-5 w-full" />
                <Skeleton className="h-5 w-full" />
                <Skeleton className="h-5 w-full" />
              </div>
            ) : (
              <div className="space-y-4">
                {recentActivity?.created_at?.length > 0 ? (
                  recentActivity.created_at.map((date, index) => (
                    <div key={index} className="flex justify-between items-center py-2 border-b dark:border-b-white/50 last:border-0">
                      <span className="font-medium">Login session</span>
                      <span className="text-sm text-muted-foreground">
                        {new Date(date).toLocaleDateString(undefined, {
                          year: "numeric",
                          month: "short",
                          day: "numeric",
                          hour: "2-digit",
                          minute: "2-digit",
                        })}
                      </span>
                    </div>
                  ))
                ) : (
                  <p className="text-muted-foreground text-center py-4">No recent activity found</p>
                )}
              </div>
            )}
          </CardContent>
        </Card>


      </div>
    </div>
  );
}
